import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TClaimStatus } from '@/@types/claims';

// LOCAL_TYPE_JUSTIFIED: claim summary shape returned by listCompanySummary — different from full IClaim
interface IClaimSummaryItem {
  _id: string;
  claimNumber: string;
  claimantName: string;
  claimantId: string;
  submittedBy: string;
  totalAmountClaimed: number;
  currency: string;
  dateOfService: number;
  status: string;
  createdAt: number;
}

const STATUS_OPTIONS: Array<{ value: TClaimStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'additional_info_required', label: 'Info Required' },
  { value: 'approved', label: 'Approved' },
  { value: 'partially_approved', label: 'Partially Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
  { value: 'partially_paid', label: 'Partially Paid' },
];

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function ClaimStatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'capitalize whitespace-nowrap text-xs',
        status === 'submitted' && 'bg-blue-100 text-blue-800 border-blue-200',
        status === 'under_review' && 'bg-yellow-100 text-yellow-800 border-yellow-200',
        status === 'additional_info_required' && 'bg-orange-100 text-orange-800 border-orange-200',
        status === 'approved' && 'bg-green-100 text-green-800 border-green-200',
        status === 'partially_approved' && 'bg-teal-100 text-teal-800 border-teal-200',
        status === 'rejected' && 'bg-red-100 text-red-800 border-red-200',
        status === 'paid' && 'bg-emerald-100 text-emerald-800 border-emerald-200',
        status === 'partially_paid' && 'bg-cyan-100 text-cyan-800 border-cyan-200'
      )}
    >
      {status.replace(/_/g, ' ')}
    </Badge>
  );
}

function TableSkeleton() {
  return (
    <>
      {[...Array(4)].map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-24" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-32" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-12" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-20" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export function CompanyClaimsSummaryPage() {
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  const { canViewAllPolicies } = useCompanyRole();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TClaimStatus | 'all'>('all');

  const currentUserId = useQuery(api.users.getCurrentUserId);

  const rawClaims = useQuery(
    api.insurance.claims.listCompanySummary,
    currentCompany && canViewAllPolicies ? { companyId: currentCompany._id } : 'skip'
  );

  const isLoading = companyLoading || rawClaims === undefined || currentUserId === undefined;
  const claims = useMemo(
    () => (rawClaims ?? []) as IClaimSummaryItem[], // AS_JUSTIFIED: Convex return narrowed to local IClaimSummaryItem[] matching listCompanySummary return validator
    [rawClaims]
  );

  const filtered = useMemo(() => {
    let result = claims;
    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        c => c.claimNumber.toLowerCase().includes(q) || c.claimantName.toLowerCase().includes(q)
      );
    }
    return result;
  }, [claims, statusFilter, search]);

  const myClaims = useMemo(
    () => filtered.filter(c => currentUserId && c.submittedBy === currentUserId),
    [filtered, currentUserId]
  );

  const teamClaims = useMemo(
    () => filtered.filter(c => !currentUserId || c.submittedBy !== currentUserId),
    [filtered, currentUserId]
  );

  if (!companyLoading && !currentCompany) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No company found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of a company to view claims.
          </p>
        </div>
      </div>
    );
  }

  if (!companyLoading && !canViewAllPolicies) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">Access restricted</h3>
          <p className="text-sm text-muted-foreground">
            Only admins, managers, and accountants can view the company claims summary.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Claims Summary</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Company insurance claims overview</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <label htmlFor="claims-search" className="sr-only">
          Search claims
        </label>
        <Input
          id="claims-search"
          placeholder="Search by claim # or employee..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select
          value={statusFilter}
          onValueChange={v => setStatusFilter(v as TClaimStatus | 'all')} // AS_JUSTIFIED: Select onValueChange string narrowed to TClaimStatus | 'all' — values constrained by STATUS_OPTIONS constant
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* My Claims */}
      <section className="mb-8">
        <h2 className="text-base font-semibold text-foreground mb-3">
          My Claims
          {!isLoading && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({myClaims.length})
            </span>
          )}
        </h2>
        <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Claim #</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableSkeleton />}
              {!isLoading && myClaims.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No claims found
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                myClaims.map(claim => (
                  <TableRow key={claim._id} className="bg-muted/20">
                    <TableCell className="font-medium">{claim.claimNumber}</TableCell>
                    <TableCell>
                      {formatCurrency(claim.totalAmountClaimed, claim.currency)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{claim.currency}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(claim.dateOfService)}
                    </TableCell>
                    <TableCell>
                      <ClaimStatusBadge status={claim.status} />
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/company/my-insurance/claims/${claim._id}`}>View Details</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Team Claims — GDPR-restricted summary only */}
      <section>
        <h2 className="text-base font-semibold text-foreground mb-3">
          Team Claims
          {!isLoading && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({teamClaims.length})
            </span>
          )}
        </h2>
        <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Claim #</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Date Submitted</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableSkeleton />}
              {!isLoading && teamClaims.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No team claims found
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                teamClaims.map(claim => (
                  // Not clickable — GDPR: no detail view for other employees' claims
                  <TableRow key={claim._id}>
                    <TableCell className="font-medium">{claim.claimNumber}</TableCell>
                    <TableCell className="text-muted-foreground">{claim.claimantName}</TableCell>
                    <TableCell>
                      {formatCurrency(claim.totalAmountClaimed, claim.currency)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{claim.currency}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(claim.createdAt)}
                    </TableCell>
                    <TableCell>
                      <ClaimStatusBadge status={claim.status} />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
